import {
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Check, FileSearch, PackageOpen, Truck, X } from "lucide-react";

import {
  OperationActionConfig,
  OperationActionKey,
  OperationInventoryStock,
  OperationRecord,
  OperationTab,
} from "../types";

interface OperationDataTableProps {
  tab: OperationTab;
  rows: OperationRecord[];
  loading: boolean;
  inventoryStocks: OperationInventoryStock[];
  stockTransfers?: OperationRecord[];
  onAction: (record: OperationRecord, action: OperationActionConfig) => void;
  onView: (record: OperationRecord) => void;
}

const actionByTab: Record<OperationTab, OperationActionConfig[]> = {
  requests: [
    { key: "createTransfer", label: "Create Transfer", tone: "primary" },
    { key: "cancel", label: "Cancel", tone: "warning" },
  ],
  transfers: [
    { key: "dispatch", label: "Dispatch", tone: "primary" },
    { key: "receive", label: "Receive", tone: "success" },
    { key: "cancel", label: "Cancel", tone: "warning" },
  ],
  receipts: [
    { key: "verify", label: "Verify", tone: "success" },
    { key: "cancel", label: "Cancel", tone: "warning" },
  ],
  issues: [{ key: "issue", label: "Issue", tone: "success" }],
  returns: [
    { key: "verify", label: "Approve Return", tone: "success" },
    { key: "reject", label: "Reject", tone: "warning" },
  ],
  spareUsage: [],
  invoices: [],
};

const serviceColumnTabs = new Set<OperationTab>([
  "issues",
  "returns",
  "spareUsage",
  "invoices",
]);

const amountColumnTabs = new Set<OperationTab>([
  "receipts",
  "spareUsage",
  "invoices",
]);

const terminalStatuses = new Set([
  "COMPLETED",
  "CANCELLED",
  "REJECTED",
  "PAID",
  "CLOSED",
  "RECEIVED",
  "VERIFIED",
]);

const displayStatus = (row: OperationRecord) => {
  if (row.type === "transfers") {
    if (row.status === "PENDING_DISPATCH" || row.status === "DRAFT") return "REQUESTED";
    if (row.status === "RECEIVED") return "COMPLETED";
  }

  if (row.type === "issues" && row.status === "PENDING") return "WAITING_FOR_ISSUE";
  if (row.type === "returns" && row.status === "PENDING") return "WAITING_FOR_RETURN";

  return row.status;
};

const statusColor = (status: string) => {
  const value = status.toUpperCase();

  if (["REQUESTED", "RECEIVED", "VERIFIED", "ISSUED", "COMPLETED", "PAID"].includes(value)) {
    return "success" as const;
  }
  if (
    [
      "PENDING",
      "PROCESSING",
      "DRAFT",
      "RAISED",
      "DISPATCHED",
      "WAITING_FOR_ISSUE",
      "WAITING_FOR_RETURN",
    ].includes(value)
  ) {
    return "warning" as const;
  }
  if (["CANCELLED", "REJECTED"].includes(value)) {
    return "error" as const;
  }

  return "default" as const;
};

const formatDate = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
};

const actionVariant = (tone: OperationActionConfig["tone"]) =>
  tone === "primary" || tone === "success" ? "contained" : "outlined";

const actionColor = (tone: OperationActionConfig["tone"]) =>
  tone === "warning" ? "warning" : tone === "error" ? "error" : tone;

const textValue = (...values: unknown[]) =>
  values
    .map((value) => (value === null || value === undefined ? "" : String(value)))
    .find(Boolean) || "";

const numberValue = (...values: unknown[]) => {
  const value = values.find((item) => item !== null && item !== undefined);
  const parsed = Number(value || 0);

  return Number.isFinite(parsed) ? parsed : 0;
};

const requestItems = (record: OperationRecord) => {
  const items = record.raw.items;

  return Array.isArray(items) ? (items as Record<string, unknown>[]) : [];
};

const hasSameHubStock = (
  record: OperationRecord,
  inventoryStocks: OperationInventoryStock[]
) => {
  if (record.type !== "requests") return true;

  const hubId = textValue(
    record.raw.requestHubId,
    record.raw.hubId,
    record.warehouseId
  );
  const items = requestItems(record);

  if (!hubId || !items.length) return false;

  return items.every((item) => {
    const sparePartId = textValue(item.sparePartId);
    const quantity = numberValue(
      item.quantityRequested,
      item.requestedQuantity,
      item.quantity
    );

    return inventoryStocks.some(
      (stock) =>
        stock.warehouseId === hubId &&
        stock.sparePartId === sparePartId &&
        stock.availableQuantity >= quantity
    );
  });
};

const hasActiveTransfer = (
  record: OperationRecord,
  stockTransfers: OperationRecord[] = []
) =>
  stockTransfers.some(
    (transfer) =>
      String(transfer.raw.stockRequestId || "") === record.id &&
      !["CANCELLED", "RECEIVED"].includes(transfer.status.toUpperCase())
  );

const canShowAction = (
  record: OperationRecord,
  action: OperationActionKey,
  inventoryStocks: OperationInventoryStock[],
  stockTransfers: OperationRecord[]
) => {
  const status = record.status;
  const normalized = status.toUpperCase();

  if (terminalStatuses.has(normalized)) return false;
  if (record.type === "requests" && action === "cancel") return normalized === "REQUESTED";
  if (record.type === "transfers" && action === "cancel") {
    return ["DRAFT", "PENDING_DISPATCH", "REQUESTED"].includes(normalized);
  }
  if (action === "receive") return normalized === "DISPATCHED";
  if (action === "dispatch") return ["DRAFT", "PENDING_DISPATCH", "REQUESTED"].includes(normalized);
  if (action === "verify") return ["RECEIVED", "PENDING"].includes(normalized);
  if (action === "createTransfer") {
    return normalized === "REQUESTED" && !hasActiveTransfer(record, stockTransfers);
  }
  if (action === "issue") {
    return (
      ["REQUESTED", "PROCESSING", "PENDING"].includes(normalized) &&
      hasSameHubStock(record, inventoryStocks)
    );
  }

  return true;
};

const OperationDataTable = ({
  tab,
  rows,
  loading,
  inventoryStocks,
  stockTransfers = [],
  onAction,
  onView,
}: OperationDataTableProps) => {
  const showServiceColumn = serviceColumnTabs.has(tab);
  const showAmountColumn = amountColumnTabs.has(tab);
  const showActorColumn = tab === "requests" || tab === "transfers";
  const columnCount = 7 + (showServiceColumn ? 1 : 0) + (showActorColumn ? 1 : 0) + (showAmountColumn ? 1 : 0);

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={(theme) => ({
        border: "1px solid",
        borderColor: theme.palette.mode === "dark" ? "#2a3953" : "divider",
        borderRadius: 2,
        overflowX: "auto",
        bgcolor: theme.palette.mode === "dark" ? "#111b2d" : "background.paper",
        "& .MuiTableCell-head": {
          py: 1.5,
          bgcolor: theme.palette.mode === "dark" ? "#18253a" : "action.hover",
          color: theme.palette.mode === "dark" ? "#aebbd0" : "text.secondary",
          fontSize: "0.72rem",
          fontWeight: 800,
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        },
        "& .MuiTableCell-body": { py: 1.35, borderColor: theme.palette.mode === "dark" ? "#263650" : "divider" },
        "& .MuiTableRow-root": { transition: "background-color 160ms ease", "&:hover": { bgcolor: theme.palette.mode === "dark" ? "#17243a" : "action.hover" } },
      })}
    >
      <Table sx={{ minWidth: 940 }}>
        <TableHead>
          <TableRow>
            <TableCell>Code</TableCell>
            <TableCell>Operation</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Movement</TableCell>
            {showActorColumn && (
              <TableCell>{tab === "requests" ? "Created By" : "Transferred By"}</TableCell>
            )}
            {showServiceColumn && <TableCell>Ticket / Employee</TableCell>}
            <TableCell align="right">Qty</TableCell>
            {showAmountColumn && <TableCell align="right">Amount</TableCell>}
            <TableCell>Updated</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!rows.length && (
            <TableRow>
              <TableCell colSpan={columnCount}>
                <Stack alignItems="center" py={7} color="text.secondary" spacing={1}>
                  <PackageOpen size={36} />
                  <Typography fontWeight={700}>
                    {loading ? "Loading operations..." : "No operations found"}
                  </Typography>
                </Stack>
              </TableCell>
            </TableRow>
          )}

          {rows.map((row) => {
            const actions = actionByTab[tab].filter((action) =>
              canShowAction(row, action.key, inventoryStocks, stockTransfers)
            );

            return (
              <TableRow
                hover
                key={`${row.type}-${row.id}`}
                onClick={() => onView(row)}
                sx={{ cursor: "pointer" }}
              >
                <TableCell>
                  <Typography fontWeight={800}>{row.code}</Typography>
                  {row.code === row.id && (
                    <Typography variant="caption" color="text.secondary">
                      System record
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography fontWeight={700}>{row.module}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {row.title}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={statusColor(displayStatus(row))}
                    label={displayStatus(row)}
                    sx={{ fontWeight: 700 }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {row.sourceLabel || row.warehouseLabel || row.source || row.warehouseId || "-"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.destinationLabel
                      ? `To ${row.destinationLabel}`
                      : row.destination
                        ? `To ${row.destination}`
                        : "Hub"}
                  </Typography>
                </TableCell>
                {showActorColumn && (
                  <TableCell>
                    <Typography variant="body2">
                      {row.actorLabel || row.actorId || "-"}
                    </Typography>
                  </TableCell>
                )}
                {showServiceColumn && (
                  <TableCell>
                    <Typography variant="body2">{row.ticketLabel || row.ticketId || "-"}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.technicianLabel || row.technicianId || row.vehicleId || "-"}
                    </Typography>
                  </TableCell>
                )}
                <TableCell align="right">{row.quantity ?? "-"}</TableCell>
                {showAmountColumn && (
                  <TableCell align="right">
                    {row.amount ? `Rs ${row.amount.toLocaleString()}` : "-"}
                  </TableCell>
                )}
                <TableCell>{formatDate(row.updatedAt || row.createdAt)}</TableCell>
                <TableCell align="right">
                  {actions.length ? (
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {actions.slice(0, 3).map((action) => (
                        <Button
                          key={action.key}
                          size="small"
                          variant={actionVariant(action.tone)}
                          color={actionColor(action.tone)}
                          startIcon={
                            action.key === "cancel" ? (
                              <X size={14} />
                            ) : action.key === "createTransfer" ? (
                              <Truck size={14} />
                            ) : (
                              <Check size={14} />
                            )
                          }
                          onClick={(event) => {
                            event.stopPropagation();
                            onAction(row, action);
                          }}
                        >
                          {action.label}
                        </Button>
                      ))}
                    </Stack>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<FileSearch size={14} />}
                      onClick={(event) => {
                        event.stopPropagation();
                        onView(row);
                      }}
                    >
                      View
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default OperationDataTable;
